import '../utilities/scrollIntoViewIfNeeded.js';
import { useTheme } from '@vebox/etui-system';
import '../utilities/format-ms.js';
import '../utilities/create-transition.js';
import { useRef, createElement } from 'react';
import { ellipsis } from '../utilities/ellipsis.js';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import '@vebox/etui-system-elements';
import '../Svg-0180d7b5.js';
import '../Icon/Icon.js';
import '@vebox/etui-icons';
import '../Icon/icons.js';
import '../Icon/helper.js';
import { lighten } from '@vebox/etui-colors';
import { ErrorLabel } from '../ErrorLabel/ErrorLabel.js';
import { FormItem } from '../FormItem/FormItem.js';
import '../InputLabel/InputLabel.js';
import '../InputAddon/InputAddon.js';
import Select, { components } from 'react-select';
import Control from './Control.js';
import ValueContainer from './ValueContainer.js';
import IndicatorsContainer from './IndicatorsContainer.js';
import DropdownIndicator from './DropdownIndicator.js';
import ClearIndicator from './ClearIndicator.js';
import Menu from './Menu.js';

const _excluded = ["name", "size", "label", "options", "readOnly", "disabled", "searchable", "defaultOpen", "defaultValue", "clearable", "startInputAddon", "endInputAddon", "tabIndex", "onChange", "elRef", "styles", "menuPortalTarget", "menuShouldBlockScroll", "formatOptionLabel", "menuIsOpen", "components", "value", "error", "indicatorColor"];
const {
  Option,
  SingleValue
} = components;
function SelectInput(_ref) {
  let {
      name,
      size = 'default',
      label,
      options,
      readOnly = false,
      disabled,
      searchable = false,
      defaultOpen,
      defaultValue,
      clearable,
      startInputAddon,
      endInputAddon,
      tabIndex = '0',
      onChange,
      elRef,
      styles = {},
      menuPortalTarget,
      menuShouldBlockScroll,
      formatOptionLabel,
      menuIsOpen,
      components: customComponents = {},
      value,
      error,
      indicatorColor
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const theme = useTheme();
  const menuRef = useRef(null);
  const handleOnChange = (newValue, actionMeta) => {
    if (onChange) {
      onChange(newValue, actionMeta);
    }
  };
  const components = _objectSpread2({
    Placeholder: () => null,
    IndicatorSeparator: () => null,
    Control,
    IndicatorsContainer,
    ValueContainer,
    DropdownIndicator,
    ClearIndicator,
    Menu,
    Option: (props = {}) => {
      var _props$data, _props$data$renderLab;
      return /*#__PURE__*/createElement(Option, props, ((_props$data = props.data) === null || _props$data === void 0 ? void 0 : (_props$data$renderLab = _props$data.renderLabel) === null || _props$data$renderLab === void 0 ? void 0 : _props$data$renderLab.call(_props$data)) || props.children);
    },
    SingleValue: (props = {}) => {
      var _props$data2, _props$data2$renderLa;
      return /*#__PURE__*/createElement(SingleValue, props, ((_props$data2 = props.data) === null || _props$data2 === void 0 ? void 0 : (_props$data2$renderLa = _props$data2.renderLabel) === null || _props$data2$renderLa === void 0 ? void 0 : _props$data2$renderLa.call(_props$data2)) || props.children);
    }
  }, customComponents);
  return /*#__PURE__*/createElement(FormItem, styles.root, /*#__PURE__*/createElement(Select, _extends({
    id: name,
    value: value,
    name: name,
    inputId: name,
    isClearable: clearable,
    placeholder: label || '',
    components: components,
    menuPortalTarget: menuPortalTarget,
    menuShouldBlockScroll: menuShouldBlockScroll,
    formatOptionLabel: formatOptionLabel,
    menuIsOpen: menuIsOpen,
    styles: {
      option: (_, {
        isFocused,
        isSelected,
        isDisabled,
        selectProps: {
          customStyles
        }
      }) => _objectSpread2(_objectSpread2({}, theme.typography.button), {}, {
        fontSize: theme.typography.fontSizeBase,
        borderBottom: `1px solid ${theme.palette.divider}`,
        height: '100%',
        paddingTop: theme.spacing.xs,
        paddingBottom: theme.spacing.xs,
        paddingRight: theme.spacing.xs,
        paddingLeft: theme.spacing.xs,
        backgroundColor: isDisabled ? 'transparent' : isSelected ? theme.palette.action.hover : isFocused ? lighten(theme.palette.action.hover, 0.5) : 'transparent',
        color: isDisabled ? lighten(theme.palette.text.secondary, 0.5) : isSelected || isFocused ? theme.palette.primary.main : theme.palette.text.secondary,
        outline: 'none',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        '&:last-child': {
          borderBottom: 0
        },
        '&:hover,:focus': {
          color: isDisabled ? lighten(theme.palette.text.secondary, 0.5) : theme.palette.primary.main,
          backgroundColor: isDisabled ? 'transparent' : lighten(theme.palette.action.hover, 0.5)
        }
      }, customStyles.option),
      clearIndicator: baseStyles => _objectSpread2(_objectSpread2({}, baseStyles), {}, {
        color: theme.palette.grey[100],
        cursor: 'pointer'
      }),
      input: baseStyles => _objectSpread2(_objectSpread2({}, baseStyles), {}, {
        margin: 0,
        padding: 0,
        paddingBottom: 0,
        paddingTop: 0,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.text.primary,
        caretColor: theme.palette.text.primary
      }),
      menu: (baseStyles, {
        selectProps: {
          menuIsOpen: isOpen,
          customStyles
        }
      }) => _objectSpread2(_objectSpread2({}, baseStyles), {}, {
        position: 'absolute',
        top: 'calc(100% - 1px)',
        color: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSizeBase,
        fontWeight: theme.typography.fontWeightRegular,
        boxShadow: theme.shadows[4],
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
        borderBottomRightRadius: `${theme.shape.borderRadius}px`,
        margin: 0,
        borderColor: error ? theme.palette.error.main : isOpen ? theme.palette.grey[100] : theme.palette.grey[60],
        borderRightWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '1px',
        borderTopWidth: 0,
        borderStyle: 'solid',
        textAlign: 'left',
        zIndex: theme.zIndex.selectInput
      }, customStyles.menu),
      menuList: (baseStyles, {
        selectProps: {
          customStyles
        }
      }) => _objectSpread2(_objectSpread2({}, baseStyles), {}, {
        padding: theme.spacing.xs,
        position: 'relative'
      }, customStyles.menuList),
      menuPortal: baseStyles => _objectSpread2(_objectSpread2({}, baseStyles), {}, {
        zIndex: 9999
      }),
      singleValue: () => _objectSpread2({
        margin: 0,
        position: 'absolute',
        width: '100%',
        lineHeight: 'normal',
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSizeBase,
        fontWeight: theme.typography.fontWeightBold,
        cursor: 'default'
      }, ellipsis()),
      multiValue: (baseStyles, {
        selectProps: {
          customStyles
        }
      }) => _objectSpread2(_objectSpread2({}, baseStyles), customStyles.multiValue),
      valueContainer: (baseStyles, {
        selectProps: {
          customStyles
        }
      }) => _objectSpread2(_objectSpread2({}, baseStyles), customStyles.valueContainer)
    },
    options: options,
    readOnly: readOnly,
    disabled: disabled,
    isDisabled: disabled || readOnly,
    isSearchable: searchable,
    defaultMenuIsOpen: defaultOpen,
    defaultValue: defaultValue,
    startInputAddon: startInputAddon,
    endInputAddon: endInputAddon,
    size: size,
    tabIndex: typeof tabIndex === 'string' ? parseInt(tabIndex, 10) : tabIndex,
    onChange: handleOnChange,
    customTheme: theme,
    ref: elRef
  }, rest, {
    customStyles: _objectSpread2(_objectSpread2({}, styles), {}, {
      input: _objectSpread2(_objectSpread2({}, styles === null || styles === void 0 ? void 0 : styles.input), error ? {
        borderColor: theme.palette.error.main
      } : {}),
      indicators: _objectSpread2(_objectSpread2({}, styles === null || styles === void 0 ? void 0 : styles.indicators), indicatorColor ? {
        color: indicatorColor
      } : {})
    }),
    menuRef: menuRef
  })), error && /*#__PURE__*/createElement(ErrorLabel, null, error.errorMessage));
}

export default SelectInput;
